/**
* This component uses the OrderConfirmationSummary component to render an array of orderConfirmationList for an order
*
* @module views/components/OrderConfirmationHistory
* @memberof -Common
*/
import './OrderConfirmationHistory.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';

import Tag from '@ulta/modules/Tag/Tag';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';

import ContextualTag from '../ContextualTag/ContextualTag';
import OrderConfirmationSummary from '../OrderConfirmationSummary/OrderConfirmationSummary';

/**
* Represents an Order Summary Wrapper component
*
* @method
* @param { OrderConfirmationHistoryProps} props - React properties passed from composition
* @returns JSX.Element
*/
export const OrderConfirmationHistory = function( props ){
  const {
    orderConfirmationList = defaultProps.orderConfirmationList,
    title = defaultProps.title,
    giftMessage = defaultProps.giftMessage,
    giftMessageHeadline = defaultProps.giftMessageHeadline,
    messages = defaultProps.messages,
    componentKey,
    returnsAction,
    trustedPartnersIcon,
    trustedPartnersLabel
  } = props;

  const hasShipments = hasItems( orderConfirmationList );

  if( !hasShipments ){
    return null;
  }

  return (
    <div className='OrderConfirmationHistory'>
      { messages?.map( ( data, index ) => (
        <div className='OrderConfirmationHistory__blockErrorMessage'
          key={ `${index}-${componentKey}` }
        >
          <BlockMessage
            message={ data.message }
            messageType={ data.icon }
            backgroundColor={ data.backgroundColor }
            borderColor={ data.borderColor }
            textColor={ data.textColor }
            dismissAction={ data.dismissAction }
          />
        </div>
      ) ) }
      <div className='OrderConfirmationHistory__HeadingContainer'>
        <div className='OrderConfirmationHistory__Heading'>
          <Text
            textStyle='title-5'
            htmlTag='h2'
          >
            { title }
          </Text>
          { trustedPartnersIcon && trustedPartnersLabel && (
            <div className='OrderConfirmationHistory__TrustedTag'>
              <Tag
                variant='icon'
                icon={ trustedPartnersIcon }
                message={ trustedPartnersLabel }
                textColor='content-subtle'
              />
            </div>
          ) }
        </div>
        { returnsAction?.url && (
          <Link_Huge action={ returnsAction }>
            { returnsAction.label }
          </Link_Huge>
        ) }
      </div>
      { orderConfirmationList.map( ( shipment ) => {
        const {
          orderStatus = null,
          splitCount, id,
          giftItems = [],
          bagItems = [],
          giftWithPurchaseTitle,
          trackAction = null,
          countLabel,
          giftCountLabel
        } = shipment;

        return (
          <div key={ `${componentKey}-orderConfirmationList-${id}` }>
            { orderStatus?.label && (
              <div className='OrderConfirmationHistory__StatusBlock'>
                <ContextualTag
                  label={ orderStatus.label }
                  color={ orderStatus.color }
                  icon={ orderStatus.icon }
                />
              </div>
            ) }
            <OrderConfirmationSummary
              giftItems={ giftItems }
              bagItems={ bagItems }
              giftWithPurchaseTitle={ giftWithPurchaseTitle }
              countLabel={ countLabel }
              giftCountLabel={ giftCountLabel }
              trackAction={ trackAction }
              splitCount={ splitCount }
            />
          </div>
        );
      } ) }
      { giftMessageHeadline && giftMessage && (
        <div className='OrderConfirmationHistory__GiftSection'>
          <div className='OrderConfirmationHistory__GiftSection__Heading'>
            <Text
              htmlTag='h2'
              textStyle='title-6'
            >
              { giftMessageHeadline }
            </Text>
          </div>
          <Text
            htmlTag='span'
            textStyle='body-2'
          >
            { giftMessage }
          </Text>
        </div>
      ) }
    </div>
  );
};

/**
* Property type definitions
* @typedef OrderConfirmationHistoryProps
* @type {object}
* @property {array} orderConfirmationList - a list of orderConfirmationList(or shipments) related to an order
* @property {string} title - heading message
* @property {string} componentKey - key to use for the component
* @property {string} giftMessage - message to show when an order has a gift
* @property {string} giftMessageHeadline - headline to show when an order has a gift
* @property {array} messages - message to show when order pricing has updated
* @property {string} trustedPartnersIcon - icon for marketplace trusted partner
* @property {string} trustedPartnersLabel - label for marketplace trusted partner
* @property {object} returnsAction - returns action for marketplace orders to link to Happy Returns
*
*
*/
export const propTypes =  {
  orderConfirmationList: PropTypes.array,
  title: PropTypes.string,
  giftMessage: PropTypes.string,
  giftMessageHeadline: PropTypes.string,
  componentKey: PropTypes.string,
  messages: PropTypes.array,
  returnsAction: PropTypes.object,
  trustedPartnersIcon: PropTypes.string,
  trustedPartnersLabel: PropTypes.string
};

/**
* Default values for passed properties
*
* @type {object}
* @property {array} orderConfirmationList='[]' - default value for orderConfirmationList
* @property {string} title='Order Summary' - default heading text for Order Summary
* @property {string} giftMessage='' - default message to show when an order has a gift
* @property {string} giftMessageHeadline='' - default headline to show when an order has a gift
* @property {array} messages='[]' - default message to show when order pricing has updated
*
*/

export const defaultProps =  {
  orderConfirmationList: [],
  title: 'Order Summary',
  giftMessage: '',
  giftMessageHeadline: '',
  messages: []
};

OrderConfirmationHistory.propTypes = propTypes;
OrderConfirmationHistory.defaultProps = defaultProps;

export default OrderConfirmationHistory;